import {useState} from 'react';
import {Box, Button, Heading, HStack, Separator, Spinner, Tag, Text, VStack} from '@chakra-ui/react';
import {Eye} from 'lucide-react';
import {useFileRevisions} from '../drive/queries';
import {describeDriveError} from '../drive/driveApi';
import {formatDisplayDateTime} from '../domain/naming';
import {normalizePhotoLink} from '../domain/comments';
import {
    DEFAULT_VISIBLE_REVISIONS,
    NumberedRevision,
    numberRevisions,
    selectedOlderRevision,
    visibleRevisions,
} from '../domain/revisions';
import {UserAvatar} from './UserAvatar';
import {formatFileSize} from '../utils/formatFileSize';

interface FileVersionsPanelProps {
    fileId: string;
    /** The version shown in the viewer; undefined for the current one. */
    selectedRevisionId: string | undefined;
    /** Shows a version; undefined goes back to the current one. */
    onSelect: (revisionId: string | undefined) => void;
}

/** The stored versions of a file, newest first: the latest few, or all of them on request. */
export function FileVersionsPanel({fileId, selectedRevisionId, onSelect}: FileVersionsPanelProps) {
    const revisionsQuery = useFileRevisions(fileId);
    // Kept with its file, so the next photo starts with the short list again.
    const [expandedFor, setExpandedFor] = useState<string>();
    const expanded = expandedFor === fileId;
    const revisions = numberRevisions(revisionsQuery.data);
    const shown = visibleRevisions(revisions, expanded);
    const selected = selectedOlderRevision(revisions, selectedRevisionId);

    return <VStack align="stretch" gap={3} px={4} pt={2} pb={4} color="white">
        <HStack gap={2}>
            <Heading size="sm">Verziók</Heading>
            {revisions.length > 0 && <Text fontSize="sm" color="gray.400">({revisions.length})</Text>}
        </HStack>
        <Separator borderColor="whiteAlpha.300"/>
        {revisionsQuery.isLoading &&
            <HStack color="gray.400" fontSize="sm"><Spinner size="xs"/><Text>Verziók betöltése…</Text></HStack>
        }
        {revisionsQuery.isError &&
            <Text fontSize="sm" color="red.300">
                A verziók betöltése sikertelen: {describeDriveError(revisionsQuery.error)}
            </Text>
        }
        {revisionsQuery.isSuccess && revisions.length === 0 &&
            <Text fontSize="sm" color="gray.500">Nincs elérhető verzió.</Text>
        }
        {shown.map((entry) =>
            <RevisionRow
                key={entry.revision.id}
                entry={entry}
                shown={selected ? entry === selected : entry.latest}
                onShow={() => onSelect(entry.latest ? undefined : entry.revision.id)}
            />
        )}
        {revisions.length > DEFAULT_VISIBLE_REVISIONS &&
            <Button
                size="xs"
                variant="plain"
                alignSelf="flex-start"
                px={0}
                h="auto"
                color="blue.200"
                _hover={{color: 'blue.100', textDecoration: 'underline'}}
                onClick={() => setExpandedFor(expanded ? undefined : fileId)}
            >
                {expanded ? 'Kevesebb' : `Összes (${revisions.length})`}
            </Button>
        }
    </VStack>;
}

interface RevisionRowProps {
    entry: NumberedRevision;
    /** It is the version the viewer shows. */
    shown: boolean;
    onShow: () => void;
}

function RevisionRow({entry, shown, onShow}: RevisionRowProps) {
    const {revision, number, latest} = entry;
    const modified = new Date(revision.modifiedTime);
    const user = revision.lastModifyingUser;
    const name = user?.displayName || 'Ismeretlen felhasználó';
    const size = revision.size ? Number(revision.size) : NaN;

    return <Box
        bg="whiteAlpha.50"
        borderWidth="1px"
        borderColor={shown ? 'blue.300' : 'whiteAlpha.100'}
        borderRadius="lg"
        px={3}
        py={2}
        data-revision-id={revision.id}
    >
        <HStack gap={2} align="stretch">
            <Box flex={1} minW={0}>
                <HStack gap={1.5} align="baseline">
                    <Text fontSize="sm" fontWeight="bold" fontVariantNumeric="tabular-nums">#{number}</Text>
                    <Text fontSize="sm" fontVariantNumeric="tabular-nums" truncate>
                        {Number.isNaN(modified.getTime()) ? '–' : formatDisplayDateTime(modified)}
                    </Text>
                </HStack>
                <HStack gap={1.5} mt={1}>
                    <UserAvatar size="2xs" name={name} src={normalizePhotoLink(user?.photoLink)} bg="blue.600" color="white"/>
                    <Text fontSize="xs" color="gray.200" truncate>
                        {name}{user?.me && <Text as="span" color="gray.400"> (te)</Text>}
                    </Text>
                </HStack>
                <Text fontSize="xs" color="gray.400" mt={0.5} truncate>
                    {!Number.isNaN(size) && formatFileSize(size)}
                </Text>
            </Box>
            <VStack align="flex-end" flexShrink={0} gap={1}>
                {latest && <Tag.Root size="sm" colorPalette="green" variant="solid" flexShrink={0}>
                    <Tag.Label>Legújabb</Tag.Label>
                </Tag.Root>}
                <Button
                    mt="auto"
                    size="xs"
                    variant="ghost"
                    flexShrink={0}
                    color={shown ? 'blue.200' : 'gray.300'}
                    _hover={{bg: 'whiteAlpha.200', color: 'white'}}
                    aria-pressed={shown}
                    disabled={shown}
                    _disabled={{opacity: 1, cursor: 'default'}}
                    onClick={onShow}
                    title={shown ? 'Ez a verzió látható' : `A(z) #${number}. verzió megjelenítése`}
                >
                    <Eye size={14}/>
                    {shown ? 'Látható' : 'Megtekintés'}
                </Button>
            </VStack>
        </HStack>
    </Box>;
}
